import qs from 'qs'
import axiosInstanceApiGateway from 'config/apiServiceApiGateway'
import { Get } from 'config/apiServiceConfig'
import { buildQueryParams } from 'utils/helpers/queryParams'

export type GetSupplyOrderRemarksResponseType = {
  error: {
    status: boolean
    code: number
    message: string
  }
  data: {
    id: number
    code: string
    description: string
  }[]
}

export const getSupplyOrderRemarksAPI = () =>
  Get<GetSupplyOrderRemarksResponseType>({
    url: '/api/wms/v2/supply-order/remarks',
  })

// ─── New SO Schedules endpoints (/wims/internal/v1/so-schedules) ─────────────

type SOScheduleErrorType = {
  status: boolean
  code: number
  message: string
}

type SOSchedulePaginationParamsType = {
  direction: string
  page_index: number
  page_size: number
  sort: string
}

type SOScheduleDestinationItemType = {
  location_id: number
  start_date: string
  end_date: string
}

export type GetSOSchedulesListRequestType = Partial<{
  location_id: number
  so_remarks: string
  type: string
  page_size: number
  page_index: number
  sort: string
  direction: string
}>

export type GetSOSchedulesListResponseType = {
  pagination: SOSchedulePaginationParamsType
  data: {
    id: number
    type: string
    schedule_days: string
    schedule_time: string
    so_remarks: string
    destination_count: number
    location: {
      id: number
      name: string
      type: string
    }
    created_at: string
    updated_at: string
  }[]
  error: SOScheduleErrorType
}

export const getSOSchedulesListAPI = (params: GetSOSchedulesListRequestType) =>
  axiosInstanceApiGateway.Get<GetSOSchedulesListResponseType, true>({
    url: '/wims/internal/v1/so-schedules',
    params,
    config: {
      paramsSerializer: {
        serialize: (p) => qs.stringify(p, { allowDots: true, skipNulls: true }),
      },
    },
  })

export type CreateSOScheduleRequestType = {
  location_id: number
  type: string
  schedule_days: string
  schedule_time: string
  so_remarks: string
  destinations: SOScheduleDestinationItemType[]
}

export type CreateSOScheduleResponseType = {
  error: SOScheduleErrorType
}

export const createSOScheduleAPI = (data: CreateSOScheduleRequestType) =>
  axiosInstanceApiGateway.Post<CreateSOScheduleResponseType>({
    url: '/wims/internal/v1/so-schedules',
    data,
  })

export type UpdateSOScheduleRequestType = {
  location_id: number
  type: string
  schedule_days: string
  schedule_time: string
  so_remarks: string
  destination: {
    to_create: SOScheduleDestinationItemType[]
    to_update: SOScheduleDestinationItemType[]
    to_delete: number[]
  }
}

export type UpdateSOScheduleResponseType = {
  error: SOScheduleErrorType
}

export const updateSOScheduleAPI = (id: number, data: UpdateSOScheduleRequestType) =>
  axiosInstanceApiGateway.Patch<UpdateSOScheduleResponseType>({
    url: `/wims/internal/v1/so-schedules/${id}`,
    data,
  })

export type GetSOScheduleByIdResponseType = {
  data: {
    id: number
    type: string
    location: {
      id: string
      name: string
      type: string
    }
    so_remarks: string
    schedule_days: string
    schedule_time: string
    destination_count: number
  }
  error: SOScheduleErrorType
}

export const getSOScheduleByIdAPI = (id: number) =>
  axiosInstanceApiGateway.Get<GetSOScheduleByIdResponseType, true>({
    url: `/wims/internal/v1/so-schedules/${id}`,
  })

export type GetSOScheduleDestinationsRequestType = Partial<SOSchedulePaginationParamsType>

export type GetSOScheduleDestinationsResponseType = {
  pagination: SOSchedulePaginationParamsType
  data: {
    id: number
    location: {
      id: string
      name: string
      type: string
    }
    start_date: string
    end_date: string
    status: string
    created_by: string
    created_at: string
    updated_by: string
    updated_at: string
  }[]
  error: SOScheduleErrorType
}

export const getSOScheduleDestinationsAPI = (
  soScheduleId: number,
  params: GetSOScheduleDestinationsRequestType,
) =>
  axiosInstanceApiGateway.Get<GetSOScheduleDestinationsResponseType, true>({
    url: `/wims/internal/v1/so-schedules/${soScheduleId}/destinations`,
    params,
  })

export type DeleteSOScheduleResponseType = {
  error: SOScheduleErrorType
}

export const deleteSOScheduleByIdAPI = (id: number) =>
  axiosInstanceApiGateway.Delete<DeleteSOScheduleResponseType>({
    url: `/wims/internal/v1/so-schedules/${id}`,
  })

export type DownloadSOSchedulesCsvResponseType = {
  error: SOScheduleErrorType
  chunk_data: string
}

export type DownloadSOSchedulesCsvRequestType = {
  location_ids?: number[]
  so_remarks?: string[]
}

export const downloadSOSchedulesCsvAPI = (params?: DownloadSOSchedulesCsvRequestType) =>
  axiosInstanceApiGateway.Get<DownloadSOSchedulesCsvResponseType, true>({
    url: '/wims/internal/v1/so-schedules:download',
    params,
    config: {
      paramsSerializer: {
        serialize: (p) => buildQueryParams(p).toString(),
      },
    },
  })

export type DownloadSOScheduleDestinationsCsvRequestType = {
  location_id?: number[]
  so_remarks?: string[]
}

export const downloadSOScheduleDestinationsCsvAPI = (
  params?: DownloadSOScheduleDestinationsCsvRequestType,
) =>
  axiosInstanceApiGateway.Get<DownloadSOSchedulesCsvResponseType, true>({
    url: '/wims/internal/v1/so-schedules/destinations:download',
    params,
    config: {
      paramsSerializer: {
        serialize: (p) => buildQueryParams(p).toString(),
      },
    },
  })

export type RunSOScheduleNowResponseType = {
  error: SOScheduleErrorType
}

export const runSOScheduleNowAPI = (id: number) =>
  axiosInstanceApiGateway.Post<RunSOScheduleNowResponseType>({
    url: `/wims/internal/v1/so-schedules:run`,
    data: {
      id,
    },
  })

export type GetWimsConstantsResponseType = {
  error: SOScheduleErrorType
  data: {
    schedule_types: {
      key: string
      label: string
    }[]
  }
}
export const getWimsConstantsAPI = () =>
  axiosInstanceApiGateway.Get<GetWimsConstantsResponseType, true>({
    url: `/wims/internal/v1/constants`,
  })

export type GetSOSchedulesOriginLocationsResponseType = {
  error: SOScheduleErrorType
  data: {
    id: number
    name: string
    type: string
  }[]
}

export const getSOSchedulesOriginLocationsAPI = () =>
  axiosInstanceApiGateway.Get<GetSOSchedulesOriginLocationsResponseType, true>({
    url: '/wims/internal/v1/so-schedules/origin-locations',
  })
